import { Grid } from "./s25.virtual.grid.component";
import { S25Util } from "../../util/s25-util";
import { DowGrid } from "./s25.dow.grid.component";
import { StandardSchedule } from "../../services/standard.schedule.service";
import { EventSummary } from "../s25-swarm-schedule/s25.event.summary.service";
import { signal } from "@angular/core";

export namespace DowGridUtil {
    export function getItemData(itemData: DowGrid.CustomItemData, totalColumns: number, index: number) {
        if (!totalColumns) return { left: 0, width: 0, snapOffsetX: 0, snapOffsetY: 0 };
        // DOW index + hour index = column index
        const left = ((index + itemData.startHour) / totalColumns) * 100;
        let hours = itemData.endHour - itemData.startHour;
        if (hours < 0) hours += 24; // Item spans midnight
        const width = (hours / totalColumns) * 100;

        let snapOffsetX = itemData.snapOffsetX || 0;
        let snapOffsetY = itemData.snapOffsetY || 0;

        return { left, width, snapOffsetX, snapOffsetY };
    }

    export function getHeaders<HeaderData extends Grid.CustomData>(
        visibleDows: string[],
        dows: string[],
        startHour: number,
        endHour: number,
        is24Hours: boolean,
        spanHours: number,
    ): Grid.Header<HeaderData>[] {
        const headers: Grid.Header<HeaderData>[] = [];
        const visible = new Set(visibleDows);
        for (let dow of dows) {
            const dowHeader: Grid.Header<HeaderData> = {
                id: dow,
                heading: dow,
                data: {} as HeaderData,
                subHeaders: [],
                hidden: !visible.has(dow),
                truncateOverflow: true,
            };
            for (let hour = 0; hour < 24; hour++) {
                const start = S25Util.date.toTimeStrFromHours(hour, is24Hours);
                const heading = hour % spanHours === 0 ? `${start}` : "";
                dowHeader.subHeaders.push({
                    id: `${dow}-${hour}`,
                    heading,
                    data: {} as HeaderData,
                    hidden: hour < startHour || hour >= endHour,
                });
            }
            headers.push(dowHeader);
        }

        return headers;
    }

    export function getTimesFromPosition(item: { left: number; width: number }, dows: string[]) {
        const columnCount = 24 * dows.length;

        const column = (item.left / 100) * columnCount;
        const dowIndex = S25Util.clamp(Math.floor(column / 24), 0, dows.length - 1);
        const dow = dows[dowIndex];
        // Figure out new start time, based on left and dow
        const startHour = column % 24;
        // Figure out new end time, based on start and width
        let endHour = (startHour + (item.width / 100) * columnCount) % 24;
        if (endHour <= startHour) endHour += 24;

        return { dow, startHour, endHour };
    }

    // Update width if number of days in pattern changed
    export function dowChangeResizer<ItemData extends DowGrid.CustomItemData>(
        items: Map<DowGrid._Item<ItemData>, DowGrid.SnapPosition>,
        dows: string[],
        dowColumnIndex: Map<string, number>,
    ): Map<DowGrid._Item<ItemData>, DowGrid.SnapPosition> {
        for (const [item, position] of items) {
            if (item.data._isShadow) continue; // Ignore shadows
            const { dow, startHour, endHour } = DowGridUtil.getTimesFromPosition(
                { left: position.left, width: position.width },
                dows,
            );
            if (item.data.dow.length === dow.length) continue; // Number of days is unchanged

            const resizableHours = endHour - startHour - (item.data.unResizableHours || 0);
            const resized = resizableHours * (item.data.dow.length / dow.length);
            const newEndHour = resized + (item.data.unResizableHours || 0) + startHour;

            if (!dowColumnIndex.has(dow)) continue;
            const dowIndex = dowColumnIndex.get(dow);
            const left = ((dowIndex + startHour) / (dows.length * 24)) * 100;
            const right = ((dowIndex + newEndHour) / (dows.length * 24)) * 100;

            position.width = right - left;
            position.dowGrid ??= {};
            position.dowGrid.resize = true;
        }

        return items;
    }

    // Snaps to standard schedules if they are close enough
    export function scheduleSnapper<ItemData extends DowGrid.CustomItemData>(
        items: Map<DowGrid._Item<ItemData>, DowGrid.SnapPosition>,
        selectedSchedule: StandardSchedule[],
        dows: string[],
        dowColumnIndex: Map<string, number>,
    ): Map<DowGrid._Item<ItemData>, DowGrid.SnapPosition> {
        const schedulesByDow = S25Util.array.groupBy(selectedSchedule, (schedule) => schedule.dow);

        // Snap each item
        // Only snap if all can snap
        const snaps: Map<DowGrid._Item<ItemData>, DowGrid.SnapPosition> = new Map();
        for (const [item, position] of items) {
            if (item.data._isShadow) continue; // Ignore shadows

            // Get DOW and start hour for position
            const { dow, startHour, endHour } = DowGridUtil.getTimesFromPosition(
                { left: position.left, width: position.width },
                dows,
            );
            const startMinutes = (startHour - (item.data.preHours || 0)) * 60;
            const endMinutes = (endHour - (item.data.postHours || 0)) * 60;

            // Find overlapping schedules and pick the one with the most overlap
            const overlapping = (schedulesByDow[dow] || []).filter(
                (schedule) => startMinutes <= schedule.end_time && endMinutes >= schedule.start_time,
            );
            let best: { overlap: number; schedule: StandardSchedule };
            for (const schedule of overlapping) {
                const overlap = Math.min(schedule.end_time - startMinutes, endMinutes - schedule.start_time);
                if (!best || best.overlap < overlap) best = { overlap, schedule };
            }
            if (!best) return items;

            if (!dowColumnIndex.has(dow)) return items;
            snaps.set(item, newPositionFromSchedule(item, position, dowColumnIndex.get(dow), best.schedule, dows));
        }
        for (const [item, position] of snaps) items.set(item, position);

        return items;
    }

    export function newPositionFromSchedule<ItemData extends DowGrid.CustomItemData>(
        item: DowGrid._Item<ItemData>,
        position: DowGrid.SnapPosition,
        dowIndex: number,
        schedule: StandardSchedule,
        dows: string[],
    ): DowGrid.SnapPosition {
        const newStartHour = schedule.start_time / 60;
        const newEndHour = schedule.end_time / 60;
        const left = ((dowIndex + newStartHour - (item.data.preHours || 0)) / (dows.length * 24)) * 100;
        const right = ((dowIndex + newEndHour + (item.data.postHours || 0)) / (dows.length * 24)) * 100;

        return {
            ...position,
            left,
            width: right - left,
            dowGrid: { ...(position.dowGrid || {}), schedule },
        };
    }

    export function scheduleChecker(schedule: StandardSchedule[]) {
        const byDow = S25Util.array.groupBy(schedule, (s) => s.dow);
        return function (item: DowGrid.Item<DowGrid._CustomItemData<DowGrid.CustomItemData>>) {
            if (item.candidate._isStandardSchedule) return true;
            const startHour = item.candidate.startHour + (item.candidate.preHours || 0);
            const endHour = item.candidate.endHour - (item.candidate.postHours || 0);
            const [start, end] = [Math.round(startHour * 60), Math.round(endHour * 60)];
            const schedules = byDow[item.candidate.dow] || [];
            for (const schedule of schedules) {
                if (schedule.start_time === start && schedule.end_time === end) return true;
            }
            return false;
        };
    }

    export function getStandardScheduleItems<ItemData extends DowGrid.CustomItemData>(
        standardSchedule: StandardSchedule[],
    ): DowGrid.Item<DowGrid._CustomItemData<ItemData>>[] {
        return (standardSchedule || []).map((schedule) => {
            return {
                id: `standard-schedule=${schedule.id}`,
                top: -1, // Make sure it goes all the way to the top edge
                height: 102, // Make sure it goes all the way to the bottom edge
                draggable: false,
                noInteraction: true,
                canOverlap: true,
                minimapColor: signal("rgba(128,197,255,0.15)"),
                data: {
                    dow: schedule.dow,
                    noShadows: true,
                    startHour: schedule.start_time / 60,
                    endHour: schedule.end_time / 60,
                    _isStandardSchedule: true,
                    _fitsSchedule: signal(true),
                } as DowGrid._CustomItemData<ItemData>,
            };
        });
    }

    export function generateShadows<ItemData extends DowGrid.CustomItemData>(
        item: DowGrid._Item<ItemData>,
        dowsList: string[],
        dowColumnIndex: Map<string, number>,
        is24Hours: boolean,
    ) {
        if (item.data._isStandardSchedule) return [];
        const { dow } = item.data;

        const shadows: Grid.Item<DowGrid._CustomItemData<ItemData>>[] = [];
        const dows = dowsList.filter((d) => d !== dow);
        const days = new Set(dow.split("") as EventSummary.DowChar[]);
        for (let newDow of dows) {
            if (!newDow.split("").some((day: EventSummary.DowChar) => days.has(day))) continue; // No intersection

            const shadow = {
                ...item, // Copy original item to shadow
                id: `${item.id}-shadow-${newDow}`,
                draggable: false, // Shadows are not draggable
                minimapColor: signal("#111"),
                data: { ...item.data } as unknown as DowGrid._CustomItemData<ItemData>, // Copy data to shadow
                overlapping: [],
            } as Grid._Item<DowGrid._CustomItemData<ItemData>>;
            shadow.data._isShadow = true;
            shadow.data.dow = newDow;
            shadow.data._realItem = item;
            shadow.data._fitsSchedule = signal(true);
            if ("name" in shadow.data) shadow.data.name = `${dow} - ${shadow.data.name}`; // Prefix name with original DOW
            shadow.ariaLabel = getAriaLabel(shadow, is24Hours);
            shadow.linkedItems.add(shadow.id); // Add shadow to shared set of linked items
            const shadowData = getItemData(shadow.data, dowsList.length * 24, dowColumnIndex.get(newDow));
            Object.assign(shadow, shadowData); // Assign new left and width values
            delete shadow._gridData; // Make sure shadow doesn't have same grid data object

            shadows.push(shadow);
        }

        return shadows;
    }

    export function getAriaLabel<ItemData extends DowGrid._CustomItemData<DowGrid.CustomItemData>>(
        item: DowGrid.Item<ItemData>,
        is24Hours: boolean,
    ) {
        const startTime = S25Util.date.ariaTimeString(S25Util.date.toTimeStrFromHours(item.data.startHour, is24Hours));
        const endTime = S25Util.date.ariaTimeString(S25Util.date.toTimeStrFromHours(item.data.endHour, is24Hours));
        return `${item.data.dow}, ${startTime} to ${endTime}`;
    }
}
